import { useEffect, useMemo, useState } from "react";
import { displayPhone, hasRealName } from "@shared/phone";
import { useLocation } from "wouter";
import {
  Calendar, Contact, HeartPulse, Inbox, KanbanSquare, LayoutDashboard, MessageSquare,
  Settings, Sun, UserRound, Users,
} from "lucide-react";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@client/components/ui/command";
import { Dialog, DialogContent, DialogTitle } from "@client/components/ui/dialog";
import { useContacts, usePatients } from "@client/hooks/index";
import { useDebounced } from "@client/hooks/useDebounced";

/**
 * Accent-insensitive, case-insensitive compare.
 *
 * Non-negotiable in pt-BR: nobody reaches for the acute accent while typing into a search box, so
 * "configuracoes" has to find "Configurações" and "respostas rapidas" has to find "Respostas
 * rápidas". Matching the raw strings would make half the menu unreachable by keyboard.
 */
const fold = (s: string) =>
  s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();

const NAV = [
  { href: "/app/hoje", icon: Sun, label: "Hoje", hint: "O que precisa de você agora" },
  { href: "/app/dashboard", icon: LayoutDashboard, label: "Dashboard", hint: "Números do período" },
  { href: "/app/conversas", icon: MessageSquare, label: "Conversas", hint: "Caixa de entrada" },
  { href: "/app/conversas?tab=fila", icon: Inbox, label: "Fila", hint: "Quem está esperando" },
  { href: "/app/comercial", icon: KanbanSquare, label: "Comercial", hint: "Funil de novos pacientes" },
  { href: "/app/acompanhamento", icon: HeartPulse, label: "Acompanhamento", hint: "Pacientes em cuidado" },
  { href: "/app/pacientes", icon: Users, label: "Pacientes" },
  { href: "/app/pacientes?ver=contatos", icon: Contact, label: "Contatos", hint: "Todos que escrevem para você" },
  { href: "/app/agenda", icon: Calendar, label: "Agenda" },
  { href: "/app/configuracoes", icon: Settings, label: "Configurações" },
];

/** The Configurações sections worth jumping straight to, by their URL slug (design pass, page 7). */
const SETTINGS = [
  { tab: "consultorio", label: "Dados do consultório" },
  { tab: "produtos", label: "Produtos e preços" },
  { tab: "marca", label: "Marca" },
  { tab: "canais", label: "Canais" },
  { tab: "agenda", label: "Agenda e horários" },
  { tab: "respostas", label: "Respostas rápidas" },
  { tab: "fases", label: "Fases dos quadros" },
  { tab: "atencao", label: "Precisam de atenção" },
  { tab: "assistente", label: "Assistente" },
  { tab: "roteiro", label: "Roteiro" },
  { tab: "equipe", label: "Pessoas e setores" },
  { tab: "emails", label: "E-mails" },
  { tab: "conta", label: "Minha conta" },
];

/**
 * The body owns the cmdk root rather than using `CommandDialog`, for two reasons that both come
 * down to control: the root needs `shouldFilter={false}`, and it needs its selected item driven
 * from here.
 *
 * Filtering is off because the patient and contact rows arrive already filtered by the API, and
 * cmdk's own fuzzy pass would quietly drop server results that did not also match its heuristic.
 * But switching it off also switches off the thing that *chose* the highlighted row, so Enter
 * stopped doing anything the moment the result set changed — which is exactly what it did on the
 * first run of this component. Hence the explicit `value`: whenever the rows change and whatever
 * was highlighted is no longer among them, the highlight returns to the top of the list.
 *
 * It is a separate component so its queries exist only while the palette is open — Radix unmounts
 * a closed dialog's children, so nothing here fetches in the background.
 */
function PaletteBody({ go }: { go: (href: string) => void }) {
  const [query, setQuery] = useState("");
  const search = useDebounced(query.trim(), 200);
  const searching = search.length >= 2;

  const patients = usePatients(searching ? search : undefined);
  const contacts = useContacts(searching ? search : undefined);

  const needle = fold(query.trim());
  const nav = NAV.filter((n) => !needle || fold(n.label).includes(needle));
  const settings = needle.length >= 2
    ? SETTINGS.filter((s) => fold(s.label).includes(needle))
    : [];

  const patientRows = searching ? (patients.data ?? []).slice(0, 6) : [];
  // A contact already linked to a patient is the same person twice; the patient row is the better
  // destination, so drop the duplicate rather than showing both.
  const contactRows = searching
    ? (contacts.data ?? []).filter((c) => !c.patient).slice(0, 5)
    : [];

  // Every selectable row, in the order they are rendered below.
  const order = useMemo(() => [
    ...patientRows.map((p) => `paciente-${p.id}`),
    ...contactRows.map((c) => `contato-${c.id}`),
    ...nav.map((n) => `nav-${n.href}`),
    ...settings.map((s) => `cfg-${s.tab}`),
  ], [patientRows, contactRows, nav, settings]);

  const [selected, setSelected] = useState("");
  const key = order.join("|");
  useEffect(() => {
    setSelected((current) => (order.includes(current) ? current : order[0] ?? ""));
    // Keyed on the joined string, not the array: `order` is a fresh identity on every render, so
    // depending on it directly would re-run this forever. When `key` changes, `order` has too.
  }, [key]);

  const loading = patients.isFetching || contacts.isFetching;

  return (
    <Command shouldFilter={false} value={selected} onValueChange={setSelected}
             className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5
                        [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium
                        [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]]:px-2
                        [&_[cmdk-input-wrapper]_svg]:h-4 [&_[cmdk-input-wrapper]_svg]:w-4
                        [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2.5">
      <CommandInput
        placeholder="Buscar pacientes, contatos ou páginas…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {/* Only claim there is nothing once the searches have actually come back. */}
        {order.length === 0 && !loading && (
          <CommandEmpty>Nada encontrado para “{query.trim()}”.</CommandEmpty>
        )}

        {patientRows.length > 0 && (
          <CommandGroup heading="Pacientes">
            {patientRows.map((p) => (
              <CommandItem key={p.id} value={`paciente-${p.id}`}
                           onSelect={() => go(`/app/pacientes/${p.id}`)}>
                <UserRound className="mr-2 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                <span className="flex-1 truncate">{p.name}</span>
                {p.phone && (
                  <span className="tabular text-xs text-muted-foreground">{displayPhone(p.phone)}</span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {contactRows.length > 0 && (
          <CommandGroup heading="Contatos">
            {contactRows.map((c) => (
              <CommandItem
                key={c.id}
                value={`contato-${c.id}`}
                onSelect={() => go(c.patient ? `/app/pacientes/${c.patient.id}` : `/app/pacientes/contato/${c.id}`)}
              >
                <Contact className="mr-2 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                <span className="flex-1 truncate">{hasRealName(c.name) ? c.name : displayPhone(c.phone) || "Sem nome"}</span>
                <span className="text-xs text-muted-foreground">{c.channel.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {nav.length > 0 && (
          <CommandGroup heading="Ir para">
            {nav.map((n) => (
              <CommandItem key={n.href} value={`nav-${n.href}`} onSelect={() => go(n.href)}>
                <n.icon className="mr-2 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                <span className="flex-1">{n.label}</span>
                {n.hint && (
                  <span className="hidden text-xs text-muted-foreground sm:inline">{n.hint}</span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {settings.length > 0 && (
          <CommandGroup heading="Configurações">
            {settings.map((s) => (
              <CommandItem key={s.tab} value={`cfg-${s.tab}`}
                           onSelect={() => go(`/app/configuracoes/${s.tab}`)}>
                <Settings className="mr-2 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                <span>{s.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );
}

/**
 * Global search, on ⌘K.
 *
 * `cmdk` and a complete `components/ui/command.tsx` have been installed in this project with zero
 * importers — the whole primitive was sitting there unused. This is the thing it was installed for.
 */
export function CommandPalette({ open, onOpenChange }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [, navigate] = useLocation();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== "k" || !(e.metaKey || e.ctrlKey)) return;
      // Browsers bind ⌘K to the address bar; this window wants it more.
      e.preventDefault();
      onOpenChange(!open);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 [&>button]:hidden">
        {/* Radix requires a title on every dialog; here the input is the visible label. */}
        <DialogTitle className="sr-only">Buscar</DialogTitle>
        <PaletteBody go={(href) => { onOpenChange(false); navigate(href); }} />
      </DialogContent>
    </Dialog>
  );
}
